import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, RefreshCw, Plus, Trash2, Inbox } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "closed"];

function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Check your credentials.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-6" data-testid="admin-login-page">
      <motion.form
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        onSubmit={submit}
        className="w-full max-w-sm rounded-3xl border gold-border-subtle bg-surface/70 p-9"
      >
        <div className="flex justify-center mb-7"><Logo size={48} /></div>
        <h1 className="font-serif text-2xl text-center text-[#F8F6F0] mb-1">Admin Inbox</h1>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 text-center mb-8">TradeSite Pro</p>
        {error && <p className="mb-4 text-sm text-red-400 text-center" data-testid="admin-login-error">{error}</p>}
        <input
          data-testid="admin-email-input"
          type="email" required placeholder="Admin email" value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full mb-3 px-5 py-3.5 rounded-xl bg-obsidian border border-zinc-800 text-zinc-200 placeholder-zinc-600 focus:border-gold/60 focus:outline-none transition-colors"
        />
        <input
          data-testid="admin-password-input"
          type="password" required placeholder="Password" value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full mb-6 px-5 py-3.5 rounded-xl bg-obsidian border border-zinc-800 text-zinc-200 placeholder-zinc-600 focus:border-gold/60 focus:outline-none transition-colors"
        />
        <button
          data-testid="admin-login-btn"
          disabled={busy}
          className="w-full py-3.5 rounded-full bg-gold text-obsidian font-sans font-semibold text-sm hover:bg-gold-hover transition-colors disabled:opacity-50"
        >
          {busy ? "Signing in..." : "Sign In"}
        </button>
        <Link to="/" className="block text-center mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600 hover:text-gold transition-colors">
          ← Back to site
        </Link>
      </motion.form>
    </div>
  );
}

export default function Admin() {
  const { admin, logout, authHeader } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [tab, setTab] = useState("inquiries");
  const [tForm, setTForm] = useState({ quote: "", author: "", business: "", trade: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [inq, tst] = await Promise.all([
        axios.get(`${API}/inquiries`, { headers: authHeader() }),
        axios.get(`${API}/testimonials/all`, { headers: authHeader() }),
      ]);
      setInquiries(inq.data);
      setTestimonials(tst.data);
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error("Could not load admin data.");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (admin) load();
  }, [admin, load]);

  if (!admin) return <Login />;

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/inquiries/${id}/status`, { status }, { headers: authHeader() });
      setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
    } catch {
      toast.error("Status update failed.");
    }
  };

  const addTestimonial = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/testimonials`, tForm, { headers: authHeader() });
      toast.success("Testimonial published to the site.");
      setTForm({ quote: "", author: "", business: "", trade: "" });
      load();
    } catch {
      toast.error("Could not add testimonial.");
    } finally {
      setBusy(false);
    }
  };

  const removeTestimonial = async (id) => {
    try {
      await axios.delete(`${API}/testimonials/${id}`, { headers: authHeader() });
      setTestimonials((prev) => prev.filter((t) => t.id !== id));
      toast.success("Testimonial removed.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div className="min-h-screen bg-obsidian" data-testid="admin-dashboard">
      <Seo title="Admin Inbox" description="TradeSite Pro admin inbox." noindex />
      <header className="border-b gold-border-subtle">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={34} />
            <span className="font-serif text-lg tracking-[0.15em] text-[#F8F6F0]">ADMIN INBOX</span>
          </div>
          <div className="flex items-center gap-4">
            <button data-testid="admin-refresh-btn" onClick={load} className="text-zinc-500 hover:text-gold transition-colors">
              <RefreshCw size={17} />
            </button>
            <Link to="/" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-gold transition-colors">View Site</Link>
            <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-red-400 transition-colors">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex gap-3 mb-8">
          {["inquiries", "testimonials"].map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-5 py-2.5 rounded-full font-mono text-xs uppercase tracking-[0.15em] transition-all duration-300 ${
                tab === t ? "bg-gold text-obsidian font-medium" : "border border-zinc-700 text-zinc-400 hover:border-gold/50 hover:text-gold"
              }`}
            >
              {t} {t === "inquiries" ? `(${inquiries.length})` : `(${testimonials.length})`}
            </button>
          ))}
        </div>

        {tab === "inquiries" && (
          <div className="space-y-4" data-testid="admin-inquiries-list">
            {inquiries.length === 0 && (
              <div className="rounded-2xl border border-zinc-800 bg-surface/50 p-14 text-center">
                <Inbox size={36} className="text-zinc-700 mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-zinc-500 text-sm">No proposal requests yet. They'll appear here the moment a client submits.</p>
              </div>
            )}
            {inquiries.map((inq) => (
              <motion.div
                key={inq.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid={`admin-inquiry-${inq.id}`}
                className="rounded-2xl border gold-border-subtle bg-surface/60 p-6"
              >
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-5">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="font-serif text-xl text-[#F8F6F0]">{inq.name}</h3>
                      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                        {new Date(inq.timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-400">
                      <a href={`mailto:${inq.email}`} className="text-gold hover:underline">{inq.email}</a>
                      {inq.phone && <span className="text-zinc-500"> · {inq.phone}</span>}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="px-3 py-1 rounded-full border border-gold/30 font-mono text-[10px] uppercase tracking-wider text-gold">{inq.industry}</span>
                      <span className="px-3 py-1 rounded-full border border-zinc-700 font-mono text-[10px] uppercase tracking-wider text-zinc-400">{inq.budget}</span>
                    </div>
                    {inq.features?.length > 0 && (
                      <p className="mt-3 text-xs text-zinc-500">Features: {inq.features.join(", ")}</p>
                    )}
                    {inq.message && <p className="mt-2 text-sm text-zinc-400 italic">"{inq.message}"</p>}
                  </div>
                  <div className="flex lg:flex-col gap-2 shrink-0" data-testid={`status-buttons-${inq.id}`}>
                    {STATUSES.map((s) => (
                      <button
                        key={s}
                        data-testid={`status-${s}-${inq.id}`}
                        onClick={() => setStatus(inq.id, s)}
                        className={`px-4 py-2 rounded-full font-mono text-[10px] uppercase tracking-[0.15em] transition-all duration-300 ${
                          inq.status === s
                            ? s === "new" ? "bg-gold text-obsidian" : s === "contacted" ? "bg-zinc-200 text-obsidian" : "bg-zinc-700 text-zinc-300"
                            : "border border-zinc-800 text-zinc-500 hover:border-gold/40 hover:text-gold"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {tab === "testimonials" && (
          <div className="grid lg:grid-cols-2 gap-8">
            <form onSubmit={addTestimonial} className="rounded-2xl border gold-border-subtle bg-surface/60 p-7" data-testid="admin-testimonial-form">
              <h3 className="font-serif text-2xl text-[#F8F6F0] mb-6 flex items-center gap-2"><Plus size={18} className="text-gold" /> Add a client quote</h3>
              <textarea
                data-testid="testimonial-quote-input"
                required rows={4} placeholder="The client's quote, in their words..."
                value={tForm.quote} onChange={(e) => setTForm({ ...tForm, quote: e.target.value })}
                className="w-full mb-3 px-5 py-3.5 rounded-xl bg-obsidian border border-zinc-800 text-zinc-200 placeholder-zinc-600 focus:border-gold/60 focus:outline-none transition-colors resize-none"
              />
              <input
                data-testid="testimonial-author-input"
                required placeholder="Client name" value={tForm.author}
                onChange={(e) => setTForm({ ...tForm, author: e.target.value })}
                className="w-full mb-3 px-5 py-3.5 rounded-xl bg-obsidian border border-zinc-800 text-zinc-200 placeholder-zinc-600 focus:border-gold/60 focus:outline-none transition-colors"
              />
              <input
                data-testid="testimonial-business-input"
                required placeholder="Business name" value={tForm.business}
                onChange={(e) => setTForm({ ...tForm, business: e.target.value })}
                className="w-full mb-3 px-5 py-3.5 rounded-xl bg-obsidian border border-zinc-800 text-zinc-200 placeholder-zinc-600 focus:border-gold/60 focus:outline-none transition-colors"
              />
              <input
                data-testid="testimonial-trade-input"
                placeholder="Trade (optional, e.g. Roofing)" value={tForm.trade}
                onChange={(e) => setTForm({ ...tForm, trade: e.target.value })}
                className="w-full mb-6 px-5 py-3.5 rounded-xl bg-obsidian border border-zinc-800 text-zinc-200 placeholder-zinc-600 focus:border-gold/60 focus:outline-none transition-colors"
              />
              <button
                data-testid="testimonial-submit-btn"
                disabled={busy}
                className="w-full py-3.5 rounded-full bg-gold text-obsidian font-sans font-semibold text-sm hover:bg-gold-hover transition-colors disabled:opacity-50"
              >
                {busy ? "Publishing..." : "Publish to Site"}
              </button>
            </form>
            <div className="space-y-4" data-testid="admin-testimonials-list">
              {testimonials.map((t) => (
                <div key={t.id} className="rounded-2xl border border-zinc-800 bg-surface/50 p-6 flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm text-zinc-300 italic">"{t.quote}"</p>
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                      {t.author} · {t.business}{t.trade ? ` · ${t.trade}` : ""}
                    </p>
                  </div>
                  <button
                    data-testid={`testimonial-delete-${t.id}`}
                    onClick={() => removeTestimonial(t.id)}
                    className="text-zinc-600 hover:text-red-400 transition-colors shrink-0"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
